/**
 * A deck as slides.
 *
 * One entry per unit, labelled the way the file labels it. Speaker notes arrive as an
 * `aside` carrying the label `notes` — pptx has them, odp does not, and a deck without them
 * is the ordinary case rather than a missing feature, so the notes column simply does not
 * appear.
 */
import { Empty } from "./ui";
import type { DocumentStructure, PlacedBlock } from "../lib/types";

function isNotes(placed: PlacedBlock): boolean {
  return placed.block.kind === "aside" && (placed.block.label ?? "").toLowerCase() === "notes";
}

export function SlidesView({ structure }: { structure: DocumentStructure }) {
  if (structure.blocks.length === 0) {
    return <Empty title="no slides" hint="the parse produced no blocks for this deck" />;
  }
  const units = [...new Set(structure.blocks.map((placed) => placed.origin.unit))];

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="grid gap-px bg-hair">
        {units.map((unit, index) => {
          const blocks = structure.blocks.filter((placed) => placed.origin.unit === unit);
          const notes = blocks.filter(isNotes);
          const body = blocks.filter((placed) => !isNotes(placed));
          return (
            <article key={unit} className="bg-ground px-5 py-4">
              <div className="mb-2 flex items-baseline gap-3">
                <span className="ordinal font-mono text-[11px] text-faint">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="eyebrow text-faint">
                  {blocks[0]?.origin.unit_label ?? `slide ${index + 1}`}
                </span>
              </div>
              {body.length === 0 ? (
                <p className="text-[12.5px] text-faint italic">no extractable text on this slide</p>
              ) : (
                body.map((placed, i) => (
                  <p
                    key={i}
                    className={
                      i === 0
                        ? "text-[15px] font-bold tracking-tight"
                        : "mt-1 text-[13.5px] leading-relaxed text-dim"
                    }
                  >
                    {placed.block.kind === "image"
                      ? (placed.block.alt ?? "image with no extractable text")
                      : "text" in placed.block
                        ? placed.block.text
                        : ""}
                  </p>
                ))
              )}
              {notes.length > 0 && (
                <div className="mt-3 border-s-2 border-gold ps-3">
                  <p className="eyebrow mb-0.5 text-faint">notes</p>
                  {notes.map((placed, i) => (
                    <p key={i} className="text-[12.5px] leading-relaxed text-dim">
                      {"text" in placed.block ? placed.block.text : ""}
                    </p>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
